import { ChangeDetectionStrategy, Component, computed, effect, input, signal } from '@angular/core';
import { LineViewStop, LineViewVariant, LineViewVehicle } from '../line-view.models';

// Mirror of the SCSS variables in ../line-view.component.scss:5-9.
// Keep in sync with $row-height and $segment-height-* there.
const ROW_PX = 18;
const SEG_PX = { short: 12, medium: 24, large: 48, 'extra-large': 96 } as const;
const SEG_DEFAULT_PX = 18;

interface VehicleDot {
    inventoryNumber: string;
    color: string;
    variantColor: string;
    hasWarning: boolean;
    goingDown: boolean;
    yPercent: number;
    dimmed: boolean;
}

interface StopTick {
    index: number;
    /** Pre-rounded px position so the 1 px line lands on a pixel boundary
     *  (avoids sub-pixel anti-aliasing that makes some ticks look thinner). */
    topPx: number;
}

@Component({
    selector: 'app-line-view-minimap',
    templateUrl: './line-view-minimap.component.html',
    styleUrls: ['./line-view-minimap.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LineViewMinimapComponent {
    readonly stops = input.required<LineViewStop[]>();
    readonly vehicles = input.required<LineViewVehicle[]>();
    readonly scrollContainer = input.required<HTMLElement>();
    // Hover/select state from the parent — the minimap mirrors the same dim
    // logic as the main view so vehicles stay in lockstep visually.
    readonly variants = input.required<LineViewVariant[]>();
    readonly hoveredVariantIndex = input<number | null>(null);
    readonly selectedVariantIndex = input<number | null>(null);
    readonly hoveredStopIndex = input<number | null>(null);

    private readonly _scrollTop = signal(0);
    private readonly _scrollHeight = signal(0);
    private readonly _clientHeight = signal(0);

    readonly visible = computed(() =>
        this._scrollHeight() > this._clientHeight() && this._scrollHeight() > 0
    );

    readonly viewportTopPct = computed(() => {
        const sh = this._scrollHeight();
        return sh > 0 ? (this._scrollTop() / sh) * 100 : 0;
    });

    readonly viewportHeightPct = computed(() => {
        const sh = this._scrollHeight();
        return sh > 0 ? (this._clientHeight() / sh) * 100 : 100;
    });

    readonly stopTicks = computed<StopTick[]>(() => {
        const offsets = this._stopOffsets();
        const total = this._totalContentPx();
        const railHeight = this._clientHeight();
        if (total <= 0 || railHeight <= 0) return [];
        // Snap to device-pixel boundaries so 1-px ticks render crisply at any
        // OS zoom (Windows 125 %/150 %) instead of anti-aliasing across two rows.
        const dpr = window.devicePixelRatio || 1;
        return offsets.map((y, index) => ({
            index,
            topPx: Math.round((y / total) * railHeight * dpr) / dpr,
        }));
    });

    readonly vehicleDots = computed<VehicleDot[]>(() => {
        const offsets = this._stopOffsets();
        const total = this._totalContentPx();
        if (total <= 0 || offsets.length === 0) return [];
        const hovered = this.hoveredVariantIndex();
        const selected = this.selectedVariantIndex();
        const hoveredStop = this.hoveredStopIndex();
        const stops = this.stops();
        const variants = this.variants();
        return this.vehicles().map(v => {
            const lo = Math.max(0, Math.min(offsets.length - 1, Math.floor(v.stopPosition)));
            const hi = Math.max(0, Math.min(offsets.length - 1, Math.ceil(v.stopPosition)));
            const frac = v.stopPosition - lo;
            const yMain = lo === hi
                ? offsets[lo]
                : offsets[lo] + frac * (offsets[hi] - offsets[lo]);
            return {
                inventoryNumber: v.inventoryNumber,
                color: v.color,
                variantColor: v.variantColor,
                hasWarning: v.hasWarning,
                goingDown: v.goingDown,
                yPercent: (yMain / total) * 100,
                dimmed: this._isVehicleDimmed(
                    v, hovered, selected, hoveredStop, stops, variants,
                ),
            };
        });
    });

    private readonly _stopOffsets = computed(() => {
        const stops = this.stops();
        if (stops.length === 0) return [];
        const offsets: number[] = [ROW_PX / 2];
        for (let i = 1; i < stops.length; i++) {
            const segCls = stops[i - 1].spacingClass;
            const seg = segCls ? SEG_PX[segCls] : SEG_DEFAULT_PX;
            offsets.push(offsets[i - 1] + ROW_PX / 2 + seg + ROW_PX / 2);
        }
        return offsets;
    });

    private readonly _totalContentPx = computed(() => {
        const offsets = this._stopOffsets();
        return offsets.length === 0 ? 0 : offsets[offsets.length - 1] + ROW_PX / 2;
    });

    private _draggingPointerId: number | null = null;
    private _dragStartCursorY = 0;
    private _dragStartScrollTop = 0;
    private readonly _scrollHandler = (): void => this._readScrollFrom(this.scrollContainer());

    constructor() {
        effect((onCleanup) => {
            const el = this.scrollContainer();
            if (!el) return;
            this._readScrollFrom(el);
            el.addEventListener('scroll', this._scrollHandler, { passive: true });
            const ro = new ResizeObserver(() => this._readScrollFrom(el));
            ro.observe(el);
            // Also observe the inner content. The scroll container's own box
            // doesn't resize when the user switches lines (the blade is the same
            // size), so without this we'd miss the new scrollHeight until the
            // user scrolls. The content element grows/shrinks with stop count.
            const content = el.firstElementChild;
            if (content) ro.observe(content);
            onCleanup(() => {
                el.removeEventListener('scroll', this._scrollHandler);
                ro.disconnect();
            });
        });
    }

    onPointerDown(event: PointerEvent): void {
        const rail = event.currentTarget as HTMLElement;
        const sc = this.scrollContainer();
        const railRect = rail.getBoundingClientRect();
        const yInRail = event.clientY - railRect.top;

        // Where the thumb currently sits on the rail.
        const thumbHeight = (sc.clientHeight / sc.scrollHeight) * railRect.height;
        const thumbTop    = (sc.scrollTop    / sc.scrollHeight) * railRect.height;

        // Click outside the thumb: jump so the thumb centres on the cursor first,
        // then continue with relative drag from there.
        if (yInRail < thumbTop || yInRail > thumbTop + thumbHeight) {
            const newThumbTop = Math.max(0, Math.min(railRect.height - thumbHeight, yInRail - thumbHeight / 2));
            sc.scrollTop = (newThumbTop / railRect.height) * sc.scrollHeight;
        }

        rail.setPointerCapture(event.pointerId);
        this._draggingPointerId = event.pointerId;
        this._dragStartCursorY = event.clientY;
        this._dragStartScrollTop = sc.scrollTop;
    }

    onPointerMove(event: PointerEvent): void {
        if (this._draggingPointerId !== event.pointerId) return;
        const rail = event.currentTarget as HTMLElement;
        const railHeight = rail.getBoundingClientRect().height;
        if (railHeight <= 0) return;
        const sc = this.scrollContainer();
        const dy = event.clientY - this._dragStartCursorY;
        // 1 px of cursor movement on the rail == (scrollHeight / railHeight) px of scroll,
        // which keeps the thumb pinned under the cursor exactly.
        const max = Math.max(0, sc.scrollHeight - sc.clientHeight);
        sc.scrollTop = Math.max(0, Math.min(max, this._dragStartScrollTop + dy * (sc.scrollHeight / railHeight)));
    }

    onPointerUp(event: PointerEvent): void {
        if (this._draggingPointerId === event.pointerId) {
            (event.currentTarget as HTMLElement).releasePointerCapture(event.pointerId);
            this._draggingPointerId = null;
        }
    }

    private _readScrollFrom(el: HTMLElement): void {
        this._scrollTop.set(el.scrollTop);
        this._scrollHeight.set(el.scrollHeight);
        this._clientHeight.set(el.clientHeight);
    }

    private _isVehicleDimmed(
        vehicle: LineViewVehicle,
        hoveredVariantIndex: number | null,
        selectedVariantIndex: number | null,
        hoveredStopIndex: number | null,
        stops: LineViewStop[],
        variants: LineViewVariant[],
    ): boolean {
        if (hoveredVariantIndex !== null) return vehicle.variantIndex !== hoveredVariantIndex;
        if (selectedVariantIndex !== null) return vehicle.variantIndex !== selectedVariantIndex;
        if (hoveredStopIndex !== null) {
            const variant = variants[vehicle.variantIndex];
            const stopName = stops[hoveredStopIndex]?.name?.toLowerCase();
            if (!variant || !stopName) return true;
            return !variant.stopNames.some((s) => s.toLowerCase() === stopName);
        }
        return false;
    }
}
